import React from 'react';
import DiscoveryLayout, { Faq, SourceLink } from '../../components/discovery/DiscoveryLayout';
import subscriptionPlans from '../../data/featuresBySubscriptionLevel.json';

const brands = [
  {
    id: 'lgnd',
    name: 'LGND Supply Co.: make entry rewards visible while shopping',
    href: 'https://lgndsupplyco.com/',
    source: 'LGND Supply Co. storefront',
    observation: 'LGND combines merchandise, product-level entry totals, bundles and a recurring membership offer. Its storefront places entry information beside prices, making the promotion part of the shopping experience. The October 2026 snapshot also separates individual products from higher-entry bundles.',
    takeaway: 'Show shoppers what a qualifying product earns before they add it to their cart. Give a bundle a clear product identity and entry rule rather than relying on a promotional banner to explain everything.',
    implementation: <>Clean Sweeps supports configurable purchase-entry rates and product bonuses. Its product entry widget is available on Free and above; cart displays require Gold or above. Use the <a href="/setup?tab=entry-widgets">widget setup guide</a> to place the corresponding display.</>,
  },
  {
    id: 'brand-80eighty',
    name: '80Eighty: explain the difference between product offers',
    href: 'https://www.80eighty.com/',
    source: '80Eighty storefront',
    observation: '80Eighty displays both entry totals and multiplier badges across its product assortment. In the storefront reviewed, different products carry different multipliers, so the entry value is visible at the item level rather than presented as one universal storewide rate.',
    takeaway: 'When offers vary, make the item-level calculation understandable. A shopper should be able to distinguish a standard product from a bonus offer without searching through several pages.',
    implementation: <>Configure Clean Sweeps product bonuses and verify each relevant product preview. For advanced multiplier rules, use Platinum or Diamond and follow the <a href="/setup?tab=order-multipliers">order multiplier walkthrough</a>. Test the saved awards as well as the display.</>,
  },
  {
    id: 'tuner-cult',
    name: 'Tuner Cult: connect a merchandise collection to the giveaway',
    href: 'https://tunercult.com/',
    source: 'Tuner Cult storefront and entry explanation',
    observation: 'Tuner Cult organizes products into recognizable collections and explains a simple shopping-to-entry journey. Its storefront also advertises recurring member benefits alongside one-time product purchases, including entry rewards and exclusive merchandise.',
    takeaway: 'Keep the initial purchase journey simple, then explain membership benefits separately. Merchandise quality and audience fit remain important even when a prize brings shoppers to the store.',
    implementation: <>Start with Clean Sweeps purchase entries and product displays. Membership entry features are available on Platinum and Diamond. The <a href="/setup?tab=membership">membership guide</a> explains the tracking embed and visible displays; placing an embed alone does not configure the reward.</>,
  },
  {
    id: 'enrforce',
    name: 'ENRFORCE: prepare for short campaign windows',
    href: 'https://enrforce.com/pages/how-to-enter',
    source: 'ENRFORCE campaign page',
    observation: 'The ENRFORCE page reviewed on October 9, 2026 promotes a 24-hour giveaway window and highlights the upcoming start. Its product navigation spans apparel, bundles, everyday accessories and vehicle accessories, giving the campaign several merchandise entry points.',
    takeaway: 'A short window makes advance preparation more valuable. Align the campaign clock, promotion schedule and storefront wording before publishing; customers should not have to guess when an offer begins or ends.',
    implementation: <>Use the Clean Sweeps <a href="/setup?tab=schedule-promotion">scheduled promotion walkthrough</a> and verify the published display at each boundary. Choose a plan for the monthly order volume you expect, including a concentrated launch-day spike.</>,
  },
  {
    id: 'prime-driven',
    name: 'Prime Driven: choose a rate customers can understand',
    href: 'https://primedriven.com/',
    source: 'Prime Driven storefront',
    observation: 'Prime Driven advertises weekly car giveaways and shows a spending-based entry rule beside its merchandise. In the reviewed snapshot, a $50 shirt shows two entries under a one-entry-per-$25 rule. Large multiplier numbers are not the only way to present an offer.',
    takeaway: 'Choose a consistent, explainable calculation before designing the promotion graphics. Explicitly decide how partial spending increments and excluded products will be handled in your own campaign.',
    implementation: <>Clean Sweeps provides configurable entry rates and a purchase-entry workflow. Use the <a href="/setup?tab=new-sweepstakes">campaign creation guide</a> and test your chosen rate with representative orders. Confirm that your intended rounding behavior is supported rather than assuming another brand&apos;s rules transfer unchanged.</>,
  },
  {
    id: 'own-boss',
    name: 'Own Boss Supply Co.: connect the prize to the audience',
    href: 'https://ownbosssupplyco.com/',
    source: 'Own Boss Supply Co. storefront',
    observation: 'Own Boss Supply Co. presents an equipment-focused prize package alongside branded merchandise. The reviewed OB47 page describes a dump truck, trailer and machinery, and the site gives shoppers a dedicated link to check their entries.',
    takeaway: 'Build the prize story around what your audience values, then make it easy for customers to check their participation after ordering. That follow-up experience deserves as much attention as the launch banner.',
    implementation: <>Clean Sweeps offers legacy account entry displays on Basic and above and guest lookup on Gold and above. Consult the <a href="/setup?tab=account-ext">account display guide</a> for account-type requirements and history limits. Customer displays are not substitutes for the full entrant records.</>,
  },
  {
    id: 'freedom-street-garage',
    name: 'Freedom Street Garage: document multiple entry routes',
    href: 'https://freedomstreetgarage.com/pages/official-rules',
    source: 'Freedom Street Garage FSG22 official rules',
    observation: 'The FSG22 rules describe merchandise purchases, membership packages and a mail-in method, with separate instructions for multiplier periods. The published example spans May 2026 to May 2027 and demonstrates how detailed a multi-route campaign can become.',
    takeaway: 'Write down how each entry method works and who manages it. Changes to a promotional rate should be reviewed against every applicable entry route and the published rules.',
    implementation: <>Clean Sweeps supports memberships on Platinum and Diamond, while Diamond adds its AMOE request form. The form produces a pending request; approval awards entries. Follow the <a href="/setup?tab=amoe">AMOE guide</a> and arrange any physical mail handling separately.</>,
  },
  {
    id: 'cleetus-mcfarland',
    name: 'Cleetus McFarland: plan online and event entry operations',
    href: 'https://www.cleetusmcfarland.com/pages/giveaway-rules',
    source: 'Cleetus McFarland ZR1, 2500HD and cash giveaway rules',
    observation: 'The giveaway that closed September 25, 2026 described online purchases, qualifying in-person event purchases and mail-in entries. Its rules explicitly explain how event customers provide contact details, illustrating the operational work behind a campaign that crosses sales channels.',
    takeaway: 'Before an event, decide how an eligible purchase becomes an identifiable entrant record. Train staff on the information needed and reconcile records before any drawing.',
    implementation: <>Clean Sweeps supports qualifying Shopify online and POS orders. Confirm customer identification and awarded entries in a trial transaction for each channel. This is a Shopify workflow, not a claim of compatibility with every external event payment system used by other brands.</>,
  },
  {
    id: 'lzmfg',
    name: 'LZMFG / Adam LZ: distinguish free-entry requests from awards',
    href: 'https://lzmfg.com/pages/700hp-evo-giveaway-official-rules',
    source: 'LZMFG 700HP Evo giveaway official rules',
    observation: 'The September–October 2026 700HP Evo rules describe purchase entries, an online free-entry method for specified jurisdictions, multiplier treatment and an independent administrator. They also explain how potential winners are contacted, helping participants recognize impersonation attempts.',
    takeaway: 'Explain the free-entry process as carefully as the shopping flow. Give customers clear submission instructions and tell them how authentic winner communication will happen.',
    implementation: <>With Clean Sweeps Diamond, configure the AMOE form for the intended campaign and review pending requests before approval. Keep that workflow aligned with your own published rules; the form does not automatically reproduce LZMFG&apos;s process or determine legal eligibility.</>,
  },
  {
    id: 'cboystv',
    name: 'CboysTV: make campaign limits and rules easy to find',
    href: 'https://cboystv.com/pages/current-giveaway-rules',
    source: 'CboysTV Ram giveaway rules page',
    observation: 'The CboysTV Ram giveaway example ended October 4, 2026. Its rules page links to the full terms and displays a brief summary covering the entry limit, eligibility, deadline and prize package. The site also links to past winners.',
    takeaway: 'Keep a concise campaign summary near the participation flow and link to complete rules. A reusable rules URL needs maintenance after a campaign closes so a new promotion is not described by old terms.',
    implementation: <>Clean Sweeps provides entrant management and winner selection on Basic and above. Before launch, verify any required entry cap with your administrator and product support; do not assume a storefront description enforces a cap. Preserve the final records for the campaign you actually ran.</>,
  },
];

const sections = [
  { id: 'the-model', label: 'The merchandise sweepstakes model' },
  ...brands.map(brand => ({ id: brand.id, label: brand.name.split(':')[0] })),
  { id: 'clean-sweeps-walkthrough', label: 'Build it with Clean Sweeps' },
  { id: 'launch-checks', label: 'Before launch' },
  { id: 'frequently-asked-questions', label: 'Common questions' },
];

export default function GiveawayBrandsGuide() {
  return (
    <DiscoveryLayout
      path="/guides/lgnd-80eighty-sweepstakes"
      title="How to run a merchandise sweepstakes like LGND, 80Eighty and other giveaway brands"
      description="Learn from 10 merchandise giveaway brands and build a Shopify purchase-entry campaign with Clean Sweeps, storefront displays and a defined AMOE workflow."
      eyebrow="Merchandise giveaway guide"
      sections={sections}
    >
      <section id="the-model">
        <h2>Turn a giveaway idea into a working merchandise campaign</h2>
        <p>
          If you want to run a sweepstakes like LGND or 80Eighty on Shopify,
          Clean Sweeps is built for the purchase-entry part of that experience:
          configurable entries per dollar, product bonuses, customer-facing
          displays and entrant management. Start with the purchase flow, then add
          the rewards and support your campaign needs.
        </p>
        <p>
          The ten examples below are selected for useful campaign lessons, not a
          measured popularity ranking. They show different ways to present
          merchandise, entry rewards and participant information. Observations
          reflect the linked pages reviewed on October 9, 2026; campaign offers
          can change, and dated examples are not invitations to enter.
        </p>
      </section>

      {brands.map(brand => (
        <section id={brand.id} key={brand.id}>
          <h2>{brand.name}</h2>
          <p>{brand.observation} <SourceLink href={brand.href}>{brand.source}</SourceLink>.</p>
          <p><strong>Merchant takeaway:</strong> {brand.takeaway}</p>
          <p><strong>Build with Clean Sweeps:</strong> {brand.implementation}</p>
        </section>
      ))}

      <section id="clean-sweeps-walkthrough">
        <h2>Build your first campaign with Clean Sweeps</h2>
        <p>
          Begin with one campaign and one clear entry rule. Free includes up to{' '}
          {subscriptionPlans['0'].orderLimit} processed orders per month and a
          product entry widget, so you can establish the basic purchase
          experience before adding more displays or rewards. Compare the{' '}
          <a href="/#pricing">current plans and order limits</a> against your expected volume.
        </p>
        <ol>
          <li>
            <strong>Create the campaign.</strong> Follow the{' '}
            <a href="/setup?tab=new-sweepstakes">New Sweepstakes tutorial</a> to
            configure your campaign, dates and eligible products. Prepare your
            official rules and no-purchase entry instructions with appropriate
            professional advice before launch.
          </li>
          <li>
            <strong>Set a simple starting rate.</strong> For illustration, an
            eligible $40 purchase at 10 entries per dollar earns 400 base
            entries: $40 × 10 = 400. This example assumes the entire $40 is
            eligible and no bonus applies. Verify exclusions, discounts and
            rounding against your saved settings and campaign rules.
          </li>
          <li>
            <strong>Add only the bonuses you can clearly explain.</strong>{' '}
            Configure product bonuses and review the{' '}
            <a href="/setup?tab=schedule-promotion">promotion schedule</a>.
            Gold and above adds spending-progress displays; Platinum and Diamond
            add advanced multipliers, membership and referral features. A{' '}
            <a href="/setup?tab=referrals">referral link</a> records referral
            context; opening it alone does not award entries.
          </li>
          <li>
            <strong>Place and publish the displays.</strong> Use the{' '}
            <a href="/setup?tab=entry-widgets">theme widget guide</a> for product,
            cart or lookup placement. Check the published mobile experience.
            Product and cart estimates, completed-order summaries and saved
            customer totals serve different purposes.
          </li>
          <li>
            <strong>Prepare free-entry handling.</strong> Diamond includes the{' '}
            <a href="/setup?tab=amoe">AMOE request form</a>. Set the explicit
            campaign ID, test a request and its reference code, and verify the
            pending-review-to-approval flow. Other plans need a separately
            arranged no-purchase entry process appropriate to the campaign.
          </li>
          <li>
            <strong>Verify actual awards.</strong> Run a representative qualifying
            purchase and inspect the saved entrant entries. Compare them with
            the storefront estimate and customer view. Plan who will reconcile
            all accepted entry methods and manage winner selection; Basic and
            above includes winner selection.
          </li>
        </ol>
        <p>
          Prefer a guided demonstration? Open the existing{' '}
          <SourceLink href="https://scribehow.com/shared/Creating_a_Sweepstakes_on_Shopify__cYk2mtlETGevFwDK992Heg">
            Clean Sweeps campaign creation walkthrough
          </SourceLink>.
        </p>
      </section>

      <section id="launch-checks">
        <h2>Make the entry experience ready before promoting it</h2>
        <ul>
          <li>Match the prize, dates, eligible products and advertised rates to your approved campaign rules.</li>
          <li>Confirm the no-purchase method is accessible and someone owns request review and record reconciliation.</li>
          <li>Check a normal order, a bonus offer, a non-qualifying product and each sales channel you intend to use.</li>
          <li>Explain where customers can see their entries and how your team handles entry questions.</li>
          <li>Arrange any required administration, legal review and prize fulfillment separately from installing the app.</li>
        </ul>
        <p>
          Clean Sweeps manages the software workflow. A form, winner-selection
          tool or setup service does not automatically make a promotion legally
          compliant. Our <a href="/guides/sweepstakes-administration-companies">sweepstakes administration guide</a>{' '}
          explains how to compare professional support alongside your Shopify app.
        </p>
      </section>

      <Faq items={[
          {
            question: 'What app should I use for an LGND-style Shopify sweepstakes?',
            answer: 'Clean Sweeps is a strong fit when your campaign needs automatic purchase entries, product bonuses, storefront entry displays and entrant management. Choose the plan for your order volume and required features; Diamond includes the AMOE request form. Your rules and administration still need their own review.',
          },
          {
            question: 'Does Clean Sweeps power the brands in this guide?',
            answer: 'This guide does not claim that any featured brand uses Clean Sweeps. Their public storefronts and rules provide examples of campaign mechanics. The suggested Clean Sweeps workflows describe how to build your own campaign.',
          },
          {
            question: 'Can I add rewards beyond purchase entries?',
            answer: 'Yes. Platinum and Diamond include membership, referral and confirmed-email signup entry features. Configure the qualifying action and confirm saved awards; a referral visit or an unconfirmed email request is not itself an awarded entry.',
          },
      ]} />
    </DiscoveryLayout>
  );
}
